import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../api/api';
import '../../styles/ConversationDetailsPage.css';

const ConversationDetailsPage = () => {
  const { id } = useParams();
  const location = window.location; // Access the window.location object directly
  const searchParams = new URLSearchParams(location.search);
  const conversationType = searchParams.get('type') || 'individual';
  const queryHalaqaId = searchParams.get('halaqaId') || '';
  
  const messagesEndRef = useRef(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [participant, setParticipant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [attachment, setAttachment] = useState(null);
  
  // Get saved conversation data from sessionStorage if available
  const [savedConversationData, setSavedConversationData] = useState(() => {
    try {
      const saved = sessionStorage.getItem('currentConversation');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      console.error('Error parsing saved conversation:', e);
      return null;
    }
  });

  // Helper function to get other participant from conversation
  const getOtherParticipant = (conversation) => {
    if (!conversation || !conversation.participants || conversation.participants.length === 0) {
      return null;
    }

    // Get current user ID from localStorage
    const userData = localStorage.getItem('user');
    let currentUserId = '';
    if (userData) {
      try {
        const user = JSON.parse(userData);
        currentUserId = user._id || '';
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }

    // For group conversations, just return null or conversation info
    if (conversation.type === 'group') {
      return {
        _id: null,
        name: conversation.title || 'Group Conversation',
        isGroup: true
      };
    }

    // Find the other participant (not the current user)
    const otherParticipant = conversation.participants.find(
      p => p._id !== currentUserId
    );

    return otherParticipant || null;
  };

  // Define fetchConversation outside useEffect so it can be called from the retry button
  const fetchConversation = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, get conversation details to get participants
      const convResponse = await api.get(`/messages/conversations`);
      let currentConversation = null;

      if (convResponse.data) {
        // Find the current conversation in the list
        let conversations = [];

        if (Array.isArray(convResponse.data)) {
          conversations = convResponse.data;
        } else if (convResponse.data.data && Array.isArray(convResponse.data.data)) {
          conversations = convResponse.data.data;
        } else if (convResponse.data.success && convResponse.data.data) {
          conversations = convResponse.data.data;
        }

        currentConversation = conversations.find(conv => conv._id === id);
      }

      // Now fetch messages for this conversation
      const messagesResponse = await api.get(`/messages/conversations/${id}`);
      console.log('Messages response:', messagesResponse);

      // Process messages
      let messagesData = [];
      if (messagesResponse.data) {
        if (Array.isArray(messagesResponse.data)) {
          messagesData = messagesResponse.data;
        } else if (messagesResponse.data.data && Array.isArray(messagesResponse.data.data)) {
          messagesData = messagesResponse.data.data;
        } else if (messagesResponse.data.success && messagesResponse.data.data) {
          messagesData = messagesResponse.data.data;
        }
      }
      
      // Enhance the conversation data for group conversations
      if (currentConversation && currentConversation.type === 'group') {
        console.log('Raw group conversation object:', JSON.stringify(currentConversation));
        
        // Check if we already have a valid halaqa property - could be a string ID or an object
        if (currentConversation.halaqa) {
          if (typeof currentConversation.halaqa === 'object' && currentConversation.halaqa._id) {
            // If it's an object with _id, store both the object and extract the ID
            currentConversation.halaqaObj = currentConversation.halaqa;
            currentConversation.halaqaId = currentConversation.halaqa._id;
            console.log('Found halaqaId from halaqa object:', currentConversation.halaqaId);
          } else if (typeof currentConversation.halaqa === 'string') {
            // If it's already a string ID, store it in halaqaId too for consistency
            currentConversation.halaqaId = currentConversation.halaqa;
            console.log('Found halaqaId from halaqa string:', currentConversation.halaqaId);
          }
        }
        
        // If we don't have the halaqa ID, try to get it from the conversation participants
        if ((!currentConversation.halaqa && !currentConversation.halaqaId) && 
            currentConversation.participants && currentConversation.participants.length > 0) {
          // If a participant has a halaqaId property, use that
          const participantWithHalaqa = currentConversation.participants.find(p => 
            p.studentInfo && p.studentInfo.halaqaId
          );
          
          if (participantWithHalaqa && participantWithHalaqa.studentInfo.halaqaId) {
            currentConversation.halaqaId = participantWithHalaqa.studentInfo.halaqaId;
            console.log('Found halaqaId from participant:', currentConversation.halaqaId);
          }
        }
        
        // If we still don't have the halaqa ID, try to get it from the messages
        if ((!currentConversation.halaqa && !currentConversation.halaqaId) && messagesData.length > 0) {
          // Try to find a message with relatedEntities containing a halaqa
          const messageWithHalaqa = messagesData.find(msg => 
            msg.relatedEntities && 
            Array.isArray(msg.relatedEntities) && 
            msg.relatedEntities.some(entity => entity.entityType === 'halaqa')
          );
          
          if (messageWithHalaqa) {
            const halaqaEntity = messageWithHalaqa.relatedEntities.find(
              entity => entity.entityType === 'halaqa'
            );
            
            if (halaqaEntity && halaqaEntity.entityId) {
              currentConversation.halaqaId = halaqaEntity.entityId;
              console.log('Found halaqaId from message:', halaqaEntity.entityId);
            }
          }
        }
      }

      console.log('Processed conversation:', currentConversation);
      console.log('Processed messages:', messagesData);

      // If we have found the proper halaqaId for a group conversation, save it to sessionStorage
      if (currentConversation && currentConversation.type === 'group' && 
         (currentConversation.halaqaId || currentConversation.halaqa)) {
        
        // Create an updated object for sessionStorage
        const updatedConversationData = {
          id: currentConversation._id,
          type: currentConversation.type,
          title: currentConversation.title || 'Group Conversation',
          halaqaId: currentConversation.halaqaId || currentConversation.halaqa || null
        };
        
        // Save to sessionStorage for future use
        sessionStorage.setItem('currentConversation', JSON.stringify(updatedConversationData));
      }
      
      // Set conversation data
      setConversation(currentConversation);

      // Set participant
      if (currentConversation) {
        const otherParticipant = getOtherParticipant(currentConversation);
        setParticipant(otherParticipant);
      }

      // Set messages
      setMessages(messagesData);

      // Mark conversation as read - use the correct endpoint
      try {
        await api.put(`/messages/${id}/read`);
        console.log('Marked conversation as read');
      } catch (readErr) {
        // Try the alternative endpoint format if the first one fails
        try {
          await api.put(`/messages/conversations/${id}/read`);
          console.log('Marked conversation as read (alternative endpoint)');
        } catch (altReadErr) {
          console.error('Error marking conversation as read:', readErr, altReadErr);
          // Non-critical error, don't show to user
        }
      }
    } catch (err) {
      console.error('Error fetching conversation:', err);
      if (err.response) {
        // Server returned an error response
        setError(`Failed to load conversation: ${err.response.data?.message || err.response.status}. Please try again later.`);
      } else if (err.request) {
        // The request was made but no response was received
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        // Something else went wrong
        setError('An unexpected error occurred. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch conversation data on component mount
  useEffect(() => {
    fetchConversation();
    
    // Set up polling for new messages
    const pollInterval = setInterval(() => {
      if (!loading) {
        fetchNewMessages();
      }
    }, 10000); // Poll every 10 seconds
    
    return () => clearInterval(pollInterval);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect to scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchNewMessages = async () => {
    try {
      if (messages.length === 0) return;
      
      const lastMessageId = messages[messages.length - 1]._id;
      
      if (!lastMessageId) return;
      
      const response = await api.get(`/messages/conversations/${id}/new`, {
        params: { lastMessageId }
      });
      
      if (response.data) {
        const newMessages = response.data.success 
          ? response.data.data 
          : response.data;
        
        if (Array.isArray(newMessages) && newMessages.length > 0) {
          setMessages(prev => [...prev, ...newMessages]);
          
          // Mark conversation as read
          await api.put(`/messages/conversations/${id}/read`);
        }
      }
    } catch (err) {
      console.error('Error fetching new messages:', err);
      // Don't show error for polling failures
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Process template placeholders
  const processPlaceholders = (text) => {
    if (!text) return '';

    // Basic placeholder replacements
    let processed = text;

    // For individual conversations, try to use specific names if available
    if (conversation && conversation.type !== 'group' && participant) {
      // Log participant structure for debugging
      console.log('DEBUG: Participant structure:', JSON.stringify({
        id: participant._id,
        name: participant.name,
        role: participant.role,
        hasChildren: participant.children ? true : false,
        hasParentInfo: participant.parentInfo ? true : false,
        hasChildAttendanceRecords: participant.childAttendanceRecords ? true : false,
        hasStudents: participant.students ? true : false
      }));
      
      // For parent with student conversations
      processed = processed.replace(/{{recipientName}}/g, participant.name || 'Parent');
      processed = processed.replace(/{{parentName}}/g, participant.name || 'Parent');
      
      // Try to get student name from participant data
      let studentName = 'Student';
      
      // Format 1: For attendance message conversations
      if (participant.childAttendanceRecords && participant.childAttendanceRecords.length > 0) {
        const childRecord = participant.childAttendanceRecords[0];
        if (childRecord && childRecord.studentId && childRecord.studentId.name) {
          studentName = childRecord.studentId.name;
          console.log('Found student name from childAttendanceRecords:', studentName);
        } else if (childRecord && childRecord.child && childRecord.child.name) {
          studentName = childRecord.child.name;
          console.log('Found student name from childAttendanceRecords.child:', studentName);
        }
      } 
      // Format 2: parentInfo.children (from populated parent)
      else if (participant.role === 'parent' && 
               participant.parentInfo && 
               participant.parentInfo.children && 
               participant.parentInfo.children.length > 0) {
        
        // If the children array contains populated user objects
        if (typeof participant.parentInfo.children[0] === 'object') {
          studentName = participant.parentInfo.children[0].name || 'Student';
          console.log('Found student name from parentInfo.children:', studentName);
        }
      }
      // Format 3: children (simplified format)
      else if (participant.children && participant.children.length > 0) {
        if (typeof participant.children[0] === 'object') {
          studentName = participant.children[0].name || 'Student';
          console.log('Found student name from children array:', studentName);
        }
      }
      // Format 4: students array (from AttendanceRecipientSelector)
      else if (participant.students && participant.students.length > 0) {
        studentName = participant.students[0].name || 'Student';
        console.log('Found student name from students array:', studentName);
      }
      
      processed = processed.replace(/{{studentName}}/g, studentName);
    } else {
      // For group conversations use generic terms
      processed = processed.replace(/{{recipientName}}/g, 'Parent');
      processed = processed.replace(/{{parentName}}/g, 'Parent');
      processed = processed.replace(/{{studentName}}/g, 'Student');
    }
    
    console.log('Processed message placeholders:', processed);
    return processed;
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!messageText.trim() && !attachment) {
      return;
    }

    // Process the message text to replace any placeholders
    const processedMessageText = processPlaceholders(messageText);

    setSending(true);
    
    // Extract the required IDs for conversation participants to handle both individual and group messages
    const userData = localStorage.getItem('user');
    let currentUser = null;
    try {
      currentUser = userData ? JSON.parse(userData) : null;
    } catch (e) {
      console.error('Failed to parse user data:', e);
    }
    
    // Dump ALL conversation data for debugging
    console.log('DEBUGGING ALL CONVERSATION DATA:');
    console.log('Current URL:', window.location.href);
    console.log('Conversation ID:', id);
    console.log('Conversation Type from URL:', conversationType);
    console.log('Halaqa ID from URL:', queryHalaqaId);
    console.log('Saved Conversation Data:', savedConversationData);
    console.log('Conversation Object:', conversation);
    console.log('Current User:', currentUser);
    
    // For individual conversations, extract the recipient ID directly from participants
    let recipientId = null;
    if (conversation && conversation.participants && conversation.type !== 'group') {
      // Extract the participant who is not the current user
      const otherParticipant = conversation.participants.find(p => 
        currentUser && p._id !== currentUser._id
      );
      if (otherParticipant) {
        recipientId = otherParticipant._id;
        console.log('Found recipient ID:', recipientId);
      }
    }
      
    // For group conversations, extract the halaqaId more aggressively
    let halaqaId = null;
    if (conversation && conversation.type === 'group') {
      // Try every possible source of halaqaId
      halaqaId = conversation.halaqa || 
                 conversation.halaqaId || 
                 queryHalaqaId || 
                 (savedConversationData?.halaqaId) ||
                 (conversation._id); // As a last resort, use conversation ID
                 
      console.log('Extracted halaqa ID from all sources:', halaqaId);
    }

    try {
      let response;
      
      // SIMPLIFIED APPROACH: Direct API calls that match the mobile implementation exactly
      if (conversation && conversation.type === 'group') {
        // === GROUP MESSAGE ===
        if (!halaqaId) {
          throw new Error('Could not determine class ID for this group conversation.');
        }
        
        if (attachment) {
          // For messages with attachments, use FormData
          const formData = new FormData();
          formData.append('halaqaId', halaqaId);
          formData.append('content', processedMessageText.trim());
          formData.append('attachment', attachment);
          
          console.log('Sending group message with attachment');
          
          response = await api.post('/messages/group', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } else {
          // For simple text messages, use direct JSON API
          console.log('Sending group message with exact mobile app parameters:', {
            halaqaId: halaqaId,
            content: processedMessageText.trim()
          });
          
          response = await api.post('/messages/group', {
            halaqaId: halaqaId,
            content: processedMessageText.trim()
          });
        }
        
        console.log('Group message response:', response);
      } else {
        // === INDIVIDUAL MESSAGE ===
        if (!recipientId) {
          throw new Error('Could not determine recipient for this message.');
        }
        
        if (attachment) {
          // For messages with attachments, use FormData
          const formData = new FormData();
          formData.append('recipient', recipientId);
          formData.append('content', processedMessageText.trim());
          formData.append('attachment', attachment);
          
          console.log('Sending individual message with attachment');
          
          response = await api.post('/messages', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } else {
          // For simple text messages, use direct JSON API
          console.log('Sending individual message with exact mobile app parameters:', {
            recipient: recipientId,
            content: processedMessageText.trim()
          });
          
          response = await api.post('/messages', {
            recipient: recipientId,
            content: processedMessageText.trim()
          });
        }
        
        console.log('Individual message response:', response);
      }

      // Regardless of response, add a "local" message to the UI for immediate feedback
      const newMessage = {
        _id: 'temp-' + Date.now(),
        content: processedMessageText, // Use the processed message text with placeholders replaced
        sender: {
          _id: 'me', // This will make the message appear as sent by the current user
          name: 'You'
        },
        createdAt: new Date().toISOString(),
        isLocal: true // Mark as local so we can handle it differently if needed
      };

      setMessages(prev => [...prev, newMessage]);
      setMessageText('');
      setAttachment(null);

      // Clear file input
      const fileInput = document.getElementById('attachment');
      if (fileInput) {
        fileInput.value = '';
      }

      // Scroll to bottom after message is sent
      setTimeout(scrollToBottom, 100);

      // Refresh the conversation in the background to get the actual saved message
      setTimeout(() => {
        fetchConversation();
      }, 1000);
    } catch (err) {
      console.error('Error sending message:', err);
      
      // Show very specific error message for debugging
      let errorMessage = 'Failed to send message.';
      
      if (err.response) {
        // Try to extract the most detailed error message possible
        const responseData = err.response.data;
        errorMessage = `API Error (${err.response.status}): ${
          responseData?.message || 
          (typeof responseData === 'string' ? responseData : JSON.stringify(responseData)) || 
          'Unknown server error'
        }`;
        
        console.error('Response error details:', {
          status: err.response.status,
          headers: err.response.headers,
          data: err.response.data
        });
      } else if (err.request) {
        errorMessage = 'Network error: No response received from server. Check your connection.';
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      // Alert with detailed information
      alert(errorMessage);
      
      // For group messaging errors, show additional debug info
      if (conversation && conversation.type === 'group') {
        console.warn('GROUP MESSAGING DEBUG INFO:');
        console.warn('- Conversation ID:', id);
        console.warn('- Extracted Halaqa ID:', halaqaId);
        console.warn('- Message Content:', messageText.trim());
        console.warn('- Conversation Type:', conversation.type);
        console.warn('- Error:', errorMessage);
      }
    } finally {
      setSending(false);
    }
  };

  const handleAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachment(file);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Helper function to determine if the message was sent by the current user
  const isCurrentUser = (sender) => {
    // If the sender is 'me' string, it's a local message we added
    if (sender === 'me') return true;

    // If the sender has _id === 'me', it's also our local message
    if (sender && sender._id === 'me') return true;

    // Get current user ID from localStorage
    const userData = localStorage.getItem('user');
    let currentUserId = '';
    if (userData) {
      try {
        const user = JSON.parse(userData);
        currentUserId = user._id || '';

        // Check if sender matches current user ID
        if (sender && (sender._id === currentUserId || sender === currentUserId)) {
          return true;
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }

    return false;
  };

  const getFileIcon = (fileUrl) => {
    if (!fileUrl) return null;

    const extension = fileUrl.split('.').pop().toLowerCase();

    switch (extension) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️';
      case 'mp3':
      case 'wav':
        return '🔊';
      case 'mp4':
      case 'mov':
        return '🎥';
      default:
        return '📎';
    }
  };

  return (
    <div className="conversation-details-container">
      <div className="header">
        <Link to="/messages" className="back-btn">
          Back to Messages
        </Link>
        <h1>
          {participant
            ? participant.isGroup
              ? participant.name
              : `Conversation with ${participant.name}`
            : conversation && conversation.type === 'group'
              ? conversation.title || 'Group Conversation'
              : 'Conversation'}
        </h1>
      </div>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading conversation...</p>
        </div>
      ) : error ? (
        <div className="error">
          <div className="error-icon">❌</div>
          <p>{error}</p>
          <button
            className="retry-button"
            onClick={fetchConversation}
          >
            Try Again
          </button>
        </div>
      ) : (
        <div className="conversation-content">
          <div className="messages-container">
            {messages.length > 0 ? (
              <div className="messages-list">
                {messages.map(message => (
                  <div
                    key={message._id}
                    className={`message ${isCurrentUser(message.sender) ? 'sent' : 'received'}`}
                  >
                    <div className="message-content">
                      <div className="message-text">{message.content}</div>
                      
                      {message.attachmentUrl && (
                        <div className="message-attachment">
                          <a 
                            href={message.attachmentUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="attachment-link"
                          >
                            <span className="attachment-icon">
                              {getFileIcon(message.attachmentUrl)}
                            </span>
                            <span className="attachment-name">
                              {message.attachmentUrl.split('/').pop()}
                            </span>
                          </a>
                        </div>
                      )}
                      
                      <div className="message-timestamp">
                        {formatDate(message.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            ) : (
              <div className="no-messages">
                No messages yet. Start the conversation!
              </div>
            )}
          </div>
          
          <div className="message-input-container">
            <form onSubmit={handleSendMessage}>
              <div className="attachment-input">
                <input
                  type="file"
                  id="attachment"
                  onChange={handleAttachmentChange}
                />
                <label htmlFor="attachment" className="attachment-label">
                  📎
                </label>
                {attachment && (
                  <span className="attachment-name">
                    {attachment.name}
                  </span>
                )}
              </div>
              
              <div className="message-input-field">
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Type your message..."
                  rows="3"
                  disabled={sending}
                ></textarea>
                <button 
                  type="submit" 
                  className="send-btn"
                  disabled={sending || (!messageText.trim() && !attachment)}
                >
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationDetailsPage;